import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import { MatrixFlowDiagram } from "@/components/site/MatrixFlowDiagram";
import HudCorners from "@/components/site/_partials/HudCorners";
import { SectionEyebrow, SectionTitle, FadeUp } from "@/components/site/_shared";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import {
  organizationSchema,
  websiteSchema,
  breadcrumbSchema,
  intentOgImage,
  BRAND, SITE_URL} from "@/lib/seo";
import {
  Stethoscope,
  Barbell,
  Buildings,
  Handshake,
  ChartLineUp,
  UsersThree,
  ShieldCheck,
  Plugs,
} from "@phosphor-icons/react";

const AUDIENCES = [
  {
    icon: Stethoscope,
    code: "ORG.01",
    title: "Medical Practices",
    body: "Add a precision-medicine layer to your practice without building it: Matrix Engine synthesis of labs, DNA, microbiome and wearables — your clinicians keep every decision, we supply the intelligence infrastructure.",
    points: ["Multi-omic patient reports", "Telehealth-ready workflows", "Physician-first design"],
  },
  {
    icon: Barbell,
    code: "ORG.02",
    title: "Gyms & Training Facilities",
    body: "Turn memberships into measured outcomes. Offer your members data-driven optimization programs — body-composition tracking, wearable integration and coaching intelligence under your roof.",
    points: ["Member optimization programs", "Body-scan integration", "Community-directory listing"],
  },
  {
    icon: Buildings,
    code: "ORG.03",
    title: "Employers",
    body: "Executive health and workforce wellness that actually measures something: baselines, physician-reviewed protocols and retesting — reported at the population level without exposing individual data.",
    points: ["Executive health programs", "Aggregate outcome reporting", "Privacy-first architecture"],
  },
  {
    icon: Handshake,
    code: "ORG.04",
    title: "Wellness Partners",
    body: "Recovery studios, nutrition brands, longevity clinics: plug your services into an ecosystem that routes motivated, data-driven members — including listing in our goal-rated community directory.",
    points: ["Co-branded programs", "Ecosystem referrals", "4,000+ venue directory"],
  },
];

const CAPABILITIES = [
  { icon: ChartLineUp, label: "Integrated Matrix Engine analysis" },
  { icon: UsersThree, label: "Licensed physician network" },
  { icon: Plugs, label: "Wearable & lab integrations" },
  { icon: ShieldCheck, label: "Secure, role-restricted data" },
];

const STEPS = [
  { step: "01", label: "Intro Call", desc: "Tell us about your organization, members and goals." },
  { step: "02", label: "Pilot Design", desc: "We scope a pilot — programs, integrations, reporting — sized to your context." },
  { step: "03", label: "Launch & Measure", desc: "Go live with clinical oversight in place; outcomes reported from day one." },
];

export default function Organizations() {
  useDocumentMeta({
    title: "For Organizations — Clinical Intelligence Infrastructure | Integrated Wellness",
    description:
      "Clinical-intelligence infrastructure for medical practices, gyms, employers and wellness partners: Matrix Engine analysis, physician network, wearable integrations and measured member outcomes.",
    image: intentOgImage("organizations"),
    url: `${SITE_URL}/organizations`,
    schema: [
      organizationSchema(),
      websiteSchema(),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "For Organizations", url: "/organizations" },
      ]),
    ],
  });

  return (
    <div data-testid="organizations-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />

      <main className="pt-28 md:pt-32 pb-20 md:pb-28">
        {/* Hero */}
        <section className="relative max-w-6xl mx-auto px-4 md:px-8">
          <div className="absolute inset-0 iw-grid-bg opacity-40 -z-10" aria-hidden="true" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="iw-overline">For Organizations</div>
              <h1 className="mt-4 font-display font-bold tracking-tight leading-[1.05] text-[clamp(2.25rem,5vw,4rem)] text-primary max-w-4xl">
                Clinical intelligence, <span className="text-accent">as infrastructure</span>.
              </h1>
              <p className="mt-6 max-w-2xl text-lg md:text-xl text-foreground/80 leading-relaxed">
                The same platform behind Integrated Wellness — the Matrix Engine, physician network,
                wearable integrations and measured programs — available to practices, gyms, employers
                and wellness partners.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a
                  href="/contact"
                  data-testid="org-hero-cta"
                  className="iw-btn-red inline-flex items-center gap-2 px-8 py-4 text-[0.72rem]"
                >
                  Talk to Partnerships <span aria-hidden>→</span>
                </a>
                <a
                  href={`mailto:${BRAND.contact.email}?subject=Organization%20Partnership%20Inquiry`}
                  data-testid="org-email-cta"
                  className="iw-btn-ghost inline-flex items-center gap-2 px-7 py-4 text-[0.72rem]"
                >
                  {BRAND.contact.email}
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="iw-scan-sweep relative aspect-[4/3] overflow-hidden border border-border bg-foreground">
                <img
                  src="https://iwmc-body-app.emergent.host/img/system/scan-room.webp"
                  alt="Integrated Wellness body-scan facility — clinical intelligence infrastructure in operation"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  style={{ filter: "brightness(1.05) contrast(1.05) saturate(1.05)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#11296B]/45 to-transparent pointer-events-none" />
                <HudCorners offset={12} size={12} />
              </div>
            </div>
          </div>
        </section>

        {/* Audiences */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-20 md:mt-28">
          <FadeUp className="text-center max-w-2xl mx-auto">
            <SectionEyebrow>Who We Partner With</SectionEyebrow>
            <SectionTitle className="mt-4 text-[clamp(1.9rem,4vw,3rem)]">
              Built for <em className="not-italic text-accent">four kinds</em> of organizations.
            </SectionTitle>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {AUDIENCES.map(({ icon: Icon, code, title, body, points }, i) => (
              <FadeUp key={code} delay={i * 0.06}>
                <div
                  data-testid={`org-audience-${code.toLowerCase().replace(".", "-")}`}
                  className="iw-track-card relative flex flex-col h-full p-8 md:p-9"
                >
                  <HudCorners offset={10} size={10} />
                  <div className="flex items-center justify-between">
                    <Icon size={30} weight="duotone" className="text-accent" />
                    <span className="font-mono text-[0.6rem] tracking-[0.22em] text-muted-foreground">
                      {code}
                    </span>
                  </div>
                  <h2 className="mt-5 font-display text-xl md:text-2xl font-bold leading-tight text-primary">
                    {title}
                  </h2>
                  <p className="mt-3 text-base text-foreground/75 leading-relaxed flex-1">{body}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {points.map((p) => (
                      <span
                        key={p}
                        className="border border-border bg-background py-1.5 px-3 font-mono text-[0.58rem] uppercase tracking-[0.12em] text-primary font-semibold"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        {/* Platform capabilities + diagram */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-20 md:mt-28">
          <FadeUp className="text-center max-w-2xl mx-auto">
            <SectionEyebrow>The Platform</SectionEyebrow>
            <SectionTitle className="mt-4 text-[clamp(1.9rem,4vw,3rem)]">
              One pipeline, <em className="not-italic text-accent">your members</em>.
            </SectionTitle>
          </FadeUp>
          <div data-testid="org-capabilities" className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
            {CAPABILITIES.map(({ icon: Icon, label }) => (
              <div key={label} className="border border-border bg-card p-5 text-center">
                <Icon size={26} weight="duotone" className="text-accent mx-auto" />
                <p className="mt-3 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-primary font-semibold leading-relaxed">
                  {label}
                </p>
              </div>
            ))}
          </div>
          <FadeUp delay={0.1} className="mt-14">
            <MatrixFlowDiagram compact />
          </FadeUp>
        </section>

        {/* How it works */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-20 md:mt-28">
          <FadeUp>
            <SectionEyebrow>How It Works</SectionEyebrow>
          </FadeUp>
          <div data-testid="org-steps" className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {STEPS.map((s) => (
              <FadeUp key={s.step}>
                <div className="border border-border bg-card p-7 h-full">
                  <span className="font-mono text-[0.65rem] tracking-[0.26em] text-accent font-bold">
                    {s.step}
                  </span>
                  <h3 className="mt-3 font-display text-lg font-bold text-primary">{s.label}</h3>
                  <p className="mt-2 text-base text-foreground/70 leading-relaxed">{s.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-20 md:mt-28">
          <FadeUp className="border-2 border-primary/25 bg-card p-10 md:p-14 text-center">
            <div className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">
              Let&rsquo;s design your pilot.
            </div>
            <p className="mt-4 text-base md:text-lg text-foreground/70 max-w-xl mx-auto">
              Tell us about your organization — we&rsquo;ll scope a pilot with clinical oversight,
              integrations and outcome reporting built in.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a
                href="/contact"
                data-testid="org-bottom-cta"
                className="iw-btn-red inline-flex items-center gap-2 px-8 py-4 text-[0.72rem]"
              >
                Talk to Partnerships <span aria-hidden>→</span>
              </a>
              <a
                href="tel:+12015353582"
                className="iw-btn-ghost inline-flex items-center gap-2 px-7 py-4 text-[0.72rem]"
              >
                (201) 535-3582
              </a>
            </div>
          </FadeUp>
        </section>
      </main>

      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
